import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import CleanContentRenderer from '../../../atoms/CleanContentRenderer/CleanContentRenderer';
import ArticleModalExternalEmbeds from './ArticleModalExternalEmbeds';
import ArticleModalHostedVideo from './ArticleModalHostedVideo';
import ArticleModalPrimaryImage from './ArticleModalPrimaryImage';
import ArticleModalTagsSection from './ArticleModalTagsSection';

const rendererSx = {
  width: '100%',
  maxWidth: '100%',
  overflow: 'hidden',
  boxSizing: 'border-box'
};

const getAuthorLabel = (article) => (
  ['YouTube', 'LinkedIn', 'TikTok'].includes(article.mediaType)
    ? (article.siteName || article.author || 'Autor não encontrado')
    : (article.author || article.siteName || 'Autor não encontrado')
);

const ArticleModalContentRenderer = ({ article }) => {
  if (article.mediaType === 'LinkedIn') return null;

  const hasRenderableContent = article.content ||
    (article.allImages && article.allImages.length > 0) ||
    article.embedCode;

  if (!hasRenderableContent) {
    return (
      <Typography variant="body1" color="text.secondary">
        Conteúdo não disponível
      </Typography>
    );
  }

  const socialMediaTypes = ['Twitter', 'Instagram', 'Facebook', 'TikTok', 'Threads'];
  const isSocialMedia = socialMediaTypes.includes(article.mediaType);

  if (isSocialMedia) {
    if (article.mediaType === 'TikTok') return null;
    if (article.mediaType === 'Facebook' && article.videoStorageMethod === 'cloudinary') return null;
    if (article.mediaType === 'Instagram' && article.embedCode) return null;

    const hasEmbed = article.mediaType === 'Twitter' ||
      (article.mediaType === 'Instagram' && article.embedCode);
    const imagesToShow = hasEmbed
      ? (article.allImages || [])
      : (article.allImages?.filter((image) => image !== article.image) || []);

    if (imagesToShow.length === 0) return null;

    return (
      <CleanContentRenderer
        contentBlocks={null}
        htmlContent={null}
        allImages={imagesToShow}
        carouselMedia={article.carouselMedia}
        mediaType={article.mediaType}
        contentType={article.contentType}
        embedHtml={article.embedCode}
        videoStorageMethod={article.videoStorageMethod}
        sx={rendererSx}
      />
    );
  }

  if (!Array.isArray(article.content)) {
    const contentStr = String(article.content).trim();
    if (contentStr.startsWith('{') || contentStr.startsWith('[')) {
      return null;
    }
  }

  return (
    <CleanContentRenderer
      contentBlocks={Array.isArray(article.content) ? article.content : null}
      htmlContent={!Array.isArray(article.content) ? article.content : null}
      allImages={article.allImages?.filter((image) => image !== article.image) || []}
      carouselMedia={article.carouselMedia}
      mediaType={article.mediaType}
      contentType={article.contentType}
      embedHtml={article.embedCode}
      videoStorageMethod={article.videoStorageMethod}
      sx={rendererSx}
    />
  );
};

const ArticleModalStandardContent = ({
  article,
  fontSize,
  isOpen,
  onAddTag,
  onOpenFullscreen,
  onRemoveTag,
  readOnly,
  videoRef,
  youtubeId
}) => (
  <>
    <ArticleModalExternalEmbeds article={article} isOpen={isOpen} />

    <ArticleModalHostedVideo
      article={article}
      videoRef={videoRef}
      youtubeId={youtubeId}
    />

    <ArticleModalPrimaryImage
      article={article}
      youtubeId={youtubeId}
      onOpenFullscreen={onOpenFullscreen}
    />

    {(article.author || article.siteName) && (
      <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' }, mb: 1, fontWeight: 500 }}>
        Por: {getAuthorLabel(article)}
      </Typography>
    )}

    {article.mediaType === 'TikTok' && article.embedCode && (
      <Box
        sx={{
          my: 2,
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          maxWidth: '100%',
          mx: 'auto'
        }}
        key={`tiktok-${article._id}-${article.embedCode?.length}`}
      >
        <CleanContentRenderer
          contentBlocks={null}
          htmlContent={null}
          allImages={[]}
          mediaType={article.mediaType}
          contentType={article.contentType}
          embedHtml={article.embedCode}
          videoStorageMethod={article.videoStorageMethod}
          sx={{ width: '100%', boxSizing: 'border-box' }}
        />
      </Box>
    )}

    {article.mediaType === 'Facebook' && article.embedCode &&
      article.videoStorageMethod !== 'cloudinary' && (
        <Box sx={{ my: 2, display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Box
            dangerouslySetInnerHTML={{ __html: article.embedCode }}
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              '& iframe': {
                width: '100%',
                maxWidth: '100%',
                borderRadius: '8px'
              }
            }}
          />
        </Box>
      )}

    {article.description && (
      <Box sx={{ my: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, fontSize: '1.1rem' }}>
          Resumo
        </Typography>
        <Typography variant="body1" sx={{
          bgcolor: 'action.hover',
          p: 2,
          borderRadius: 1,
          fontStyle: 'italic',
          borderLeft: 4,
          borderColor: 'primary.main',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          maxHeight: 200,
          overflow: 'auto'
        }}>
          {article.description}
        </Typography>
      </Box>
    )}

    {article.transcript && (
      <Box sx={{ my: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'secondary.main', fontWeight: 600, fontSize: '1.1rem' }}>
          Transcricao
        </Typography>
        <Typography variant="body2" sx={{
          bgcolor: 'action.hover',
          p: 2,
          borderRadius: 1,
          borderLeft: 4,
          borderColor: 'secondary.main',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          maxHeight: 400,
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          fontSize: '0.9rem',
          lineHeight: 1.6
        }}>
          {article.transcript}
        </Typography>
      </Box>
    )}

    <ArticleModalContentRenderer article={article} />

    <ArticleModalTagsSection
      article={article}
      fontSize={fontSize}
      onAddTag={onAddTag}
      onRemoveTag={onRemoveTag}
      readOnly={readOnly}
    />
  </>
);

const articleShape = {
  _id: PropTypes.string,
  allImages: PropTypes.array,
  author: PropTypes.string,
  carouselMedia: PropTypes.array,
  content: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.object]),
  contentType: PropTypes.string,
  description: PropTypes.string,
  embedCode: PropTypes.string,
  image: PropTypes.string,
  mediaType: PropTypes.string,
  siteName: PropTypes.string,
  transcript: PropTypes.string,
  videoStorageMethod: PropTypes.string
};

ArticleModalContentRenderer.propTypes = {
  article: PropTypes.shape(articleShape).isRequired
};

ArticleModalStandardContent.propTypes = {
  article: PropTypes.shape(articleShape).isRequired,
  fontSize: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onAddTag: PropTypes.func.isRequired,
  onOpenFullscreen: PropTypes.func.isRequired,
  onRemoveTag: PropTypes.func,
  readOnly: PropTypes.bool,
  videoRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any })
  ]).isRequired,
  youtubeId: PropTypes.string
};

ArticleModalStandardContent.defaultProps = {
  fontSize: undefined,
  onRemoveTag: null,
  readOnly: false,
  youtubeId: null
};

export default ArticleModalStandardContent;
